import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { AuthContext } from '../../../context/auth-context';
import ProductionMetricsAPI from '../../../../services/api/analytics/production-metrics-api';
import KpiTrendChart from '../components/KpiTrendChart';

const KpiTrendsTab = ({ projectId }) => {
  const auth = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await ProductionMetricsAPI.getKpiTrends(
          projectId,
          auth.token
        );
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load KPI trend data');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [projectId, auth.token]);

  if (!projectId) {
    return (
      <Typography sx={{ color: 'text.secondary', mt: 4, textAlign: 'center' }}>
        Select a project to view KPI trends.
      </Typography>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ mt: 4, textAlign: 'center' }}>
        {error}
      </Typography>
    );
  }

  if (!data || !data.weeks || data.weeks.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Alert severity="info" sx={{ maxWidth: 500 }}>
          No KPI trend data yet. Data will appear after the first weekly
          snapshot runs.
        </Alert>
      </Box>
    );
  }

  const goodDirection = data.goodDirection ?? 'below';
  const aboveIsGood = goodDirection === 'above';

  const lpfSubtitle = aboveIsGood
    ? 'Over 1.0 = under budget (good)   |   Under 1.0 = over budget'
    : 'Under 1.0 = under budget (good)   |   Over 1.0 = over budget';
  const spiSubtitle = aboveIsGood
    ? 'Over 1.0 = ahead of schedule (good)   |   Under 1.0 = behind schedule'
    : 'Under 1.0 = ahead of schedule (good)   |   Over 1.0 = behind schedule';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <KpiTrendChart
        title="Labor Productivity Factor (LPF) — Project to Date"
        weeks={data.weeks}
        valueKey="ptdLpf"
        goodDirection={goodDirection}
        subtitle={lpfSubtitle}
      />
      <KpiTrendChart
        title="Schedule Performance Index (SPI) — Project to Date"
        weeks={data.weeks}
        valueKey="ptdSpi"
        goodDirection={goodDirection}
        subtitle={spiSubtitle}
      />
    </Box>
  );
};

export default KpiTrendsTab;
